import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';

import { PageableTableComponent } from '../../shared/controls/pageable-table/pageable-table.component';

import { DialogService } from '../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../shared/toast/toast.service';
import { InitializerService } from '../../shared/services/initializer-service';
import { ErrorService } from '../../shared/services/error-service';
import { AuthService } from '../../shared/services/auth.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../shared/constants/area.constants';
import { PermissionConstants } from '../../shared/constants/permission.constants';

import { ClientService, DownloadService, FilteredClientItem, PagingData } from '../../../service/proxy';

@Component({
    templateUrl: './client.component.html',
    styleUrl: './client.component.scss',

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        HasPermissionDirective,
    ],

    providers: [
        ClientService,
        DownloadService,
    ]
})
export class ClientComponent implements OnInit {
    public showEdit: boolean = false;
    public showDelete: boolean = false;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public clients: FilteredClientItem[] = [];

    constructor(
        public authService: AuthService,
        private router: Router,
        private clientService: ClientService,
        private downloadService: DownloadService,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private cdr: ChangeDetectorRef,
        private loadingService: LoadingService
    )
    {
        this.paging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        this.initializePermissions();
    }

    initializePermissions(): void {
        this.showEdit = this.authService
            .hasPermission(AreaConstants.Client, PermissionConstants.Edit);

        this.showDelete = this.authService
            .hasPermission(AreaConstants.Client, PermissionConstants.Delete);
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getClients(this.paging);
    }

    getClients(pagingData: PagingData = new PagingData): void {
        pagingData.sortColumn = 'clientName';
        const request$ = this.clientService.getFilteredClients(
            this.search, 
            pagingData.pageNo, 
            pagingData.pageSize, 
            pagingData.sortDirection, 
            pagingData.sortColumn
        );

        LoadingHelper.withLoading(request$, this.loadingService)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.clients = data.results || [];
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.clients = [];
                        this.paging = this.initializerService.createPagingData();
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            });
    }

    getColumnMap() {
        return {
            clientName: 'Client Name',
            tradingName: 'Trading Name',
            countryName: 'Country Name',
            contactPerson: 'Contact Person',
            clientEmail: 'Client Email',
        };
    }

    upsertClient(id: number): void {
        this.router.navigate([`/client/${id}`]);
    }

    deleteClient(id: number): void {
        this.dialogService.show('Delete', 'Are you sure you want to delete this record?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.clientService.deleteClient(id)
                        .subscribe({
                            next: () => {
                                this.getClients(this.paging);
                                this.toastService.show('Client deleted successfully.', 'success');
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            }
                        });
                }
            }
        );
    }

    downloadClients(pagingData: PagingData = new PagingData): void {
        this.downloadService.downloadClients(this.search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe(response => {
                if (response.status === 200) {
                    saveAs(response.data, 'Clients.xlsx');
                }
                else {
                    alert('Download failed');
                }
            });
    }
}
